import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { CopyIcon, FileIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { openDownloadLink } from "simplydo/core/storage";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { H2, Subheader } from "simplydo/react/ui/header";
import { Icon, IconData } from "simplydo/react/ui/icon";
import {
  List,
  ListDescription,
  ListHeader,
  ListItem,
} from "simplydo/react/ui/list";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import Login from "components/entry/Login";

import { FileIconMap } from "./Notifications/Downloads";

const formatFileSize = (size: number) => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};

export const Download = () => {
  const user = useAppSelector((state) => state.user);
  const [searchParams] = useSearchParams();

  const paramDict = useMemo(() => {
    const forType = searchParams.get("forType") || undefined;
    const forId = searchParams.get("forId") || undefined;
    const identifier = searchParams.get("identifier") || undefined;
    const entityType = searchParams.get("entityType") || undefined;
    const entityId = searchParams.get("entityId") || undefined;

    return { forType, forId, identifier, entityType, entityId };
  }, [searchParams]);

  const fileDetailQuery = useQuery(
    initUseQuery(api.uploads.getDownloadDetails, [paramDict], {
      isEnabled: !!paramDict.forType && !!paramDict.forId,
    }),
  );

  const fileType = fileDetailQuery.data?.fileType;
  const fileIcon: IconData = fileType
    ? (FileIconMap[fileType as keyof typeof FileIconMap] ?? FileIcon)
    : FileIcon;

  const checkFileDownload = useCallback(() => {
    api.uploads.getFileDownloadCookies(
      paramDict,
      ({ url }) => {
        openDownloadLink(url, fileDetailQuery.data?.fileName ?? "");
      },
      (error) => {
        if (error.status === 404) {
          toast.error("This file no longer exists or has been removed.");
          return;
        }
        toast.error("Could not download file, please try again later.");
      },
    );
  }, [paramDict, fileDetailQuery.data]);

  if (!user) {
    return (
      <div>
        <H2>File download</H2>
        <Message style={{ marginTop: 0 }} color="blue">
          To download this file, please log in or sign up.
        </Message>
        <Login embedded />
      </div>
    );
  }

  if (!paramDict.forType || !paramDict.forId) {
    return (
      <div>
        <H2>File download</H2>
        <Message color="orange">
          <MessageHeader>Something went wrong</MessageHeader>
          The link to this file is invalid or the file does not exist, please
          try again. If the problem persists, please contact support.
        </Message>
        <ButtonLink color="primary" to="/">
          Go to homepage
        </ButtonLink>
      </div>
    );
  }

  if (fileDetailQuery.error) {
    return (
      <div>
        <H2>File download</H2>
        <Message style={{ marginTop: 0 }} color="red">
          This file is not available for download. The file either does not
          exist or you are not authorised to access it.
        </Message>
        <ButtonLink color="primary" to="/">
          Go to homepage
        </ButtonLink>
      </div>
    );
  }

  return (
    <div>
      <H2>
        File download
        <Subheader>
          You can download the file by clicking the download button below.
        </Subheader>
      </H2>

      {fileDetailQuery.isLoading ? (
        <div
          style={{
            minHeight: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      ) : (
        <div>
          <Message>
            <MessageHeader>
              <Icon icon={fileIcon} /> File details
            </MessageHeader>
            <List divided>
              <ListItem>
                <ListHeader>File name:</ListHeader>
                <ListDescription>
                  {fileDetailQuery.data?.fileName}
                </ListDescription>
              </ListItem>
              <ListItem>
                <ListHeader>File size:</ListHeader>
                <ListDescription>
                  {formatFileSize(fileDetailQuery.data?.fileSize ?? 0)}
                </ListDescription>
              </ListItem>
              <ListItem>
                <ListHeader>File type:</ListHeader>
                <ListDescription>{fileType}</ListDescription>
              </ListItem>
            </List>
          </Message>

          <div style={{ float: "right" }}>
            <Button
              onClick={() => {
                void util.copyToClipboard(window.location.href);
              }}
            >
              <Icon icon={CopyIcon} /> Copy link
            </Button>
            <Button onClick={() => checkFileDownload()} color="primary">
              Download
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
